import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import type { HelpContextInput } from '../data/helpGuides';
import { reportAutoError } from '../services/autoErrorReporter';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  dismissible?: boolean;
  /** Never create an auto error ticket for this toast (e.g. form validation). */
  skipReport?: boolean;
  /** Always create an auto error ticket for this toast, even if it does not look like a failure. */
  report?: boolean;
}

/**
 * Error toasts are also used for form validation and guard messages ("Pick how this booking
 * reached us (Source).", "Please select...", "X is required"). Those are not bugs, so only
 * error toasts that read like a real failure create a ticket. Backend failures are reported
 * separately by the fetch hook in lib/supabase.ts, and crashes by the global listeners.
 */
const FAILURE_TOAST_PATTERN =
  /\b(fail(s|ed|ure)?|errors?|could ?n[o']t|unable|exception|went wrong|not deployed|timed? ?out|crash(ed)?|does not exist|permission denied|violates)\b/i;

function shouldReportErrorToast(toast: Toast): boolean {
  if (toast.skipReport) return false;
  if (toast.report) return true;
  return FAILURE_TOAST_PATTERN.test(toast.message || '');
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
  showSuccess: (message: string, options?: Partial<Toast>) => string;
  showError: (message: string, options?: Partial<Toast>) => string;
  showWarning: (message: string, options?: Partial<Toast>) => string;
  showInfo: (message: string, options?: Partial<Toast>) => string;
  showLoading: (message: string, options?: Partial<Toast>) => string;
  showToast: (message: string, type: ToastType, options?: Partial<Toast>) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID();
    const newToast: Toast = {
      id,
      duration: 3000, // Default 3 seconds
      dismissible: true,
      ...toast,
    };
    
    setToasts(prev => [newToast, ...prev]);
    if (newToast.type === 'error' && shouldReportErrorToast(newToast)) {
      void reportAutoError({ message: newToast.message, action: 'Error notification shown' });
    }
    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const showSuccess = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'success', message, ...options });
  }, [addToast]);

  const showError = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'error', message, ...options });
  }, [addToast]);

  const showWarning = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'warning', message, ...options });
  }, [addToast]);

  const showInfo = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({ type: 'info', message, ...options });
  }, [addToast]);

  const showLoading = useCallback((message: string, options?: Partial<Toast>) => {
    return addToast({
      type: 'loading',
      message,
      duration: 0, // Loading toasts don't auto-dismiss by default
      ...options
    });
  }, [addToast]);

  // Generic showToast that accepts a type parameter
  const showToast = useCallback((message: string, type: ToastType, options?: Partial<Toast>) => {
    return addToast({ type, message, ...options });
  }, [addToast]);

  const value: ToastContextType = {
    toasts,
    addToast,
    dismissToast,
    dismissAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    showToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface HelpContextValue {
  helpContext: HelpContextInput;
  setHelpContext: (ctx: HelpContextInput) => void;
  clearHelpContext: () => void;
}

const HelpContext = createContext<HelpContextValue | undefined>(undefined);

const EMPTY_HELP_CONTEXT: HelpContextInput = {};

export function HelpContextProvider({ children }: { children: ReactNode }) {
  const [helpContext, setHelpContextState] = useState<HelpContextInput>(EMPTY_HELP_CONTEXT);

  const setHelpContext = useCallback((ctx: HelpContextInput) => {
    setHelpContextState(ctx);
  }, []);

  const clearHelpContext = useCallback(() => {
    setHelpContextState(EMPTY_HELP_CONTEXT);
  }, []);

  const value = useMemo(
    () => ({ helpContext, setHelpContext, clearHelpContext }),
    [helpContext, setHelpContext, clearHelpContext]
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelpContext(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) {
    throw new Error('useHelpContext must be used within HelpContextProvider');
  }
  return ctx;
}

/** Register situational help context while a component is mounted. */
export function useRegisterHelpContext(ctx: HelpContextInput | null | undefined) {
  const { setHelpContext, clearHelpContext } = useHelpContext();

  useEffect(() => {
    if (ctx && (ctx.surface || (ctx.tags && ctx.tags.length > 0))) {
      setHelpContext(ctx);
    } else {
      clearHelpContext();
    }
    return () => clearHelpContext();
  }, [ctx?.surface, ctx?.tags?.join(','), setHelpContext, clearHelpContext]);
}